import { useEffect } from 'react';
import { useUpdate } from 'react-use';

// example:
// const [useSharedStateMy, getSharedStateMy, setSharedStateMy] = createSharedState<string>('hello');
// const myStateJS = getSharedStateMy(); // 'hello'
// setSharedStateMy('world'); // 'world'
// const [myStateReact, setMyStateReact] = useSharedMyState();
// console.log(myStateReact); // 'world'
// setMyStateReact('react'); // 'react'

export const createSharedState = <State>(
  defaultState: State,
): [() => [State, (state: State) => void], () => State, (state: State) => void] => {
  const sharedStore: {
    state: State;
    getState: () => State;
    setState: (state: State) => void;
    subscribers: { (): void }[];
  } = {
    state: defaultState,
    getState: () => sharedStore.state,
    setState: (state) => {
      sharedStore.state = state;
      sharedStore.subscribers.forEach((update) => update?.());
    },
    subscribers: [],
  };
  return [
    () => {
      const update = useUpdate();
      useEffect(() => {
        if (!sharedStore.subscribers.includes(update)) {
          sharedStore.subscribers.push(update);
        }
        return () => {
          sharedStore.subscribers = sharedStore.subscribers.filter((subscriber) => subscriber !== update);
        };
      }, [update]);
      return [sharedStore.state, sharedStore.setState];
    },
    sharedStore.getState,
    sharedStore.setState,
  ];
};

export default createSharedState;
